// One answer for money in KPIs, tables, charts and reports (O1's money rule):
// whole kronor in full with space groups up to 9 999 999 ("4 812 000 kr"); from ten
// million one decimal with the unit word of the UI language ("12.4M kr", "12,4 mn kr").
// The currency code never leads the number ("SEK 4.8M"). Table cells are always full.

const KRONA = new Set(['SEK', 'NOK', 'DKK']);
const COMPACT_FROM = 10_000_000;

/** A negative sign is always the true minus (U+2212), never a hyphen.
 * Thousand groups are always a (non-breaking) space, in every language and for every
 * number on a screen; decimals keep the language's separator ("41.8%", "12,4"). */
/** The unit word that closes a formatted amount ("kr" for kronor), if the currency has one. */
export const moneyUnitWord = (currency: string | undefined) =>
  currency && KRONA.has(currency) ? 'kr' : undefined;

export function formatNumber(
  value: number,
  locale: string,
  options: Intl.NumberFormatOptions = {},
): string {
  const english = new Intl.Locale(locale).language === 'en';
  return new Intl.NumberFormat(locale, { useGrouping: 'always' as unknown as boolean, ...options })
    .formatToParts(value)
    .map((part) =>
      part.type === 'group'
        ? '\u00a0'
        : part.type === 'minusSign'
          ? '\u2212'
          : part.type === 'compact' && english && part.value === 'm'
            ? 'M'
            : part.value,
    )
    .join('');
}

export function formatMoney(
  value: number,
  locale: string,
  currency: string,
  options: { full?: boolean; precision?: number } = {},
): string {
  const compact = !options.full && Math.abs(value) >= COMPACT_FROM;
  if (KRONA.has(currency)) {
    // Space-grouped kronor in every language (formatNumber); the decimal separator and the
    // unit word follow the UI language.
    const number = compact
      ? formatNumber(value, locale, { notation: 'compact', maximumFractionDigits: 1 })
      : formatNumber(value, locale, digits(options.precision, 0));
    return `${number} kr`;
  }
  // Other currencies keep their own symbol (narrow, never the code) in the locale's placement.
  return formatNumber(value, locale, {
    style: 'currency',
    currency,
    currencyDisplay: 'narrowSymbol',
    notation: compact ? 'compact' : 'standard',
    ...(compact ? { maximumFractionDigits: 1 } : digits(options.precision, 0)),
  });
}

/** A declared precision fixes the decimals; otherwise the currency shows whole units. */
const digits = (precision: number | undefined, fallback: number) =>
  precision === undefined
    ? { maximumFractionDigits: fallback }
    : { minimumFractionDigits: precision, maximumFractionDigits: precision };

/** Axis ticks: space-grouped in full up to 9 999 999, then compact with the language's unit
 * word ("12M", "12 mn"). The currency is named by the axis or tile title, not repeated. */
export function formatMoneyTick(value: number, locale: string): string {
  if (Math.abs(value) >= COMPACT_FROM)
    return formatNumber(value, locale, { notation: 'compact', maximumFractionDigits: 0 });
  return formatNumber(value, locale, { maximumFractionDigits: 0 });
}

/** One notation and unit for the whole axis, chosen by its largest absolute tick. */
export function moneyAxisFormatter(locale: string, maximumTick: number) {
  const maximum = Math.abs(maximumTick);
  if (maximum < COMPACT_FROM) return (value: number) => formatMoneyTick(value, locale);
  // Ask ICU for the unit at this magnitude, including languages whose compact units
  // differ from thousands/millions. Latin digits let us recover its numeric divisor.
  const reference = 10 ** Math.floor(Math.log10(maximum));
  const parts = new Intl.NumberFormat(locale, {
    notation: 'compact',
    numberingSystem: 'latn',
    maximumFractionDigits: 0,
  }).formatToParts(reference);
  const coefficient = Number(
    parts
      .filter((part) => part.type === 'integer')
      .map((part) => part.value)
      .join(''),
  );
  const divisor = reference / coefficient;
  const unit = formatNumber(divisor, locale, { notation: 'compact', maximumFractionDigits: 0 });
  const one = formatNumber(1, locale);
  return (value: number) =>
    value === 0
      ? formatNumber(0, locale)
      : unit.replace(one, formatNumber(value / divisor, locale, { maximumFractionDigits: 1 }));
}

/** The explicit fallback when a caller passes no locale: never the browser's environment. */
export const currentLocale = () => 'en-GB';
